import { useMemo } from "react"

import { Floor } from "shared/types/Floor"
import {
  isProjectFloor,
  isProjectProductPosition,
  isProjectRoom
} from "shared/types/Project"

import {
  UNASSIGNED_PRODUCTS_TAB_ID,
  UNASSIGNED_ROOMS_TAB_ID
} from "entities/project-children/lib/projectTabIDs"
import { ProjectModel } from "features/project-management/types/ProjectModel"

interface UseProjectTabStateArgs {
  projectModel?: ProjectModel | null
  requestedTabID?: string | null
}

interface UseProjectTabStateReturn {
  floors: Floor[]
  unassignedRoomsCount: number
  unassignedProductsCount: number
  activeTabID: string | null
}

export function useProjectTabState(
  args: UseProjectTabStateArgs
): UseProjectTabStateReturn {
  const { projectModel, requestedTabID } = args

  const floors = useMemo(() => {
    return (
      projectModel?.core.data.project?.data?.children?.filter(child =>
        isProjectFloor(child)
      ) ?? []
    )
  }, [projectModel])

  const unassignedRoomsCount = useMemo(() => {
    return (
      projectModel?.core.data.project?.data?.children?.filter(child =>
        isProjectRoom(child)
      ).length ?? 0
    )
  }, [projectModel])

  const unassignedProductsCount = useMemo(() => {
    return (
      projectModel?.core.data.project?.data?.children?.filter(child =>
        isProjectProductPosition(child)
      ).length ?? 0
    )
  }, [projectModel])

  const activeTabID = useMemo((): string | null => {
    if (
      requestedTabID === UNASSIGNED_PRODUCTS_TAB_ID &&
      unassignedProductsCount > 0
    ) {
      return requestedTabID
    }
    if (
      requestedTabID === UNASSIGNED_ROOMS_TAB_ID &&
      unassignedRoomsCount > 0
    ) {
      return requestedTabID
    }
    if (floors.some(floor => floor.id === requestedTabID)) {
      return requestedTabID ?? null
    }
    if (floors.length > 0) {
      return floors[0].id
    }
    if (unassignedProductsCount > 0) {
      return UNASSIGNED_PRODUCTS_TAB_ID
    }
    if (unassignedRoomsCount > 0) {
      return UNASSIGNED_ROOMS_TAB_ID
    }
    return requestedTabID ?? UNASSIGNED_PRODUCTS_TAB_ID
  }, [requestedTabID, unassignedProductsCount, unassignedRoomsCount, floors])

  return {
    floors,
    unassignedRoomsCount,
    unassignedProductsCount,
    activeTabID
  }
}
